import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import B2ChatClient, { apiResource, b2chatBaseQuery } from '@src/client';

// eslint-disable-next-line no-shadow
export enum MarketingMessagesOnboardingStatus {
  ELIGIBLE = 'ELIGIBLE',
  ONBOARDED = 'ONBOARDED',
  INELIGIBLE = 'INELIGIBLE',
  PENDING = 'PENDING',
  UNKNOWN = 'UNKNOWN',
  REQUESTED = 'REQUESTED',
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
}

export type GetWhatsAppSettingsResponse = {
  templateSettings: {
    marketingTtl?: number;
    utilityTtl?: number;
    authenticationTtl?: number;
    ringingEnabled: boolean;
  };
};

export type UpdateWhatsAppSettingsRequest = {
  templateSettings: {
    marketingTtl?: number;
    utilityTtl?: number;
    authenticationTtl?: number;
    ringingEnabled: boolean;
  };
};

// eslint-disable-next-line no-shadow
export enum MarketingLiteStatus {
  'ONBOARDED' = 'ONBOARDED',
  'ELIGIBLE' = 'ELIGIBLE',
  'INELIGIBLE' = 'INELIGIBLE',
}
export type WhatsAppWaba = {
  wabaId: string;
  name: string;
  marketingLiteStatus: MarketingLiteStatus;
  lines: string[];
};

export type MMLiteSummary = {
  businessId: string;
  name: string;
  wabas: WhatsAppWaba[];
};

export type MMLiteSummaryFlatten = {
  businessId: string;
  name: string;
  waba: WhatsAppWaba;
};

type GetWabaStructureResponse = MMLiteSummary[];
type GetWabaStructureFlattenResponse = MMLiteSummaryFlatten[];

const convertTtl = (ttl: number | undefined, divisor: number) =>
  (ttl ?? 0) / divisor || 0;

export const whatsAppApi = createApi({
  reducerPath: 'whatsapp',
  baseQuery: b2chatBaseQuery,
  tagTypes: ['whatsapp-settings'],
  endpoints: ({ query, mutation }) => ({
    getWhatsAppSettings: query<GetWhatsAppSettingsResponse, void>({
      providesTags: ['whatsapp-settings'],
      query: () =>
        apiResource(
          B2ChatClient.resources.whatsapp.actions.retrieveSettings,
          {}
        ),
      transformResponse: (response: GetWhatsAppSettingsResponse) => {
        const { marketingTtl, utilityTtl, authenticationTtl, ringingEnabled } =
          response.templateSettings;

        return {
          templateSettings: {
            marketingTtl: convertTtl(marketingTtl, 60 * 60 * 24),
            utilityTtl: convertTtl(utilityTtl, 60 * 60),
            authenticationTtl: convertTtl(authenticationTtl, 60),
            ringingEnabled,
          },
        };
      },
    }),
    updateWhatsAppSettings: mutation<void, UpdateWhatsAppSettingsRequest>({
      invalidatesTags: ['whatsapp-settings'],
      query: data =>
        apiResource(B2ChatClient.resources.whatsapp.actions.updateSettings, {
          data,
        }),
    }),
    getWabaStructure: query<GetWabaStructureFlattenResponse, void>({
      query: () =>
        apiResource(
          B2ChatClient.resources.whatsapp.actions.getWabaStructure,
          {}
        ),
      transformResponse: (response: GetWabaStructureResponse) =>
        response.reduce((prev, cur) => {
          const wabas: WhatsAppWaba[] = cur.wabas.map(
            ({ wabaId, name, marketingLiteStatus, lines }) => ({
              wabaId,
              name,
              marketingLiteStatus,
              lines,
            })
          );
          return [
            ...prev,
            ...wabas.map(curW => ({
              businessId: cur.businessId,
              name: cur.name,
              waba: curW,
            })),
          ];
        }, [] as GetWabaStructureFlattenResponse),
    }),
  }),
});

export const {
  useGetWhatsAppSettingsQuery,
  useLazyGetWhatsAppSettingsQuery,
  useUpdateWhatsAppSettingsMutation,
  useGetWabaStructureQuery,
  useLazyGetWabaStructureQuery,
} = whatsAppApi;

export const selectWabaStructure =
  whatsAppApi.endpoints.getWabaStructure.select();

export const selectIsMMLiteConnected = createSelector(
  selectWabaStructure,
  postResult =>
    postResult.data?.some(
      item => item.waba.marketingLiteStatus === MarketingLiteStatus.ONBOARDED
    )
);
